/**
 * Visitor region detection — decides what a *logged-out* visitor sees
 * (language, default country, Spanish landing) before a shop is known.
 *
 * Once a user is inside a shop, the shop's own `country` wins (see
 * `LocaleContext`). This module never calls a third-party geo service;
 * it only reads signals the browser already has plus an optional
 * `sl_country` cookie that nginx can set from a self-hosted GeoIP DB
 * (see deploy/nginx-geoip.conf).
 *
 * Precedence:
 *   1. `sl_country` cookie (nginx GeoIP2, when enabled on the droplet)
 *   2. Browser timezone (Europe/Madrid, Atlantic/Canary, Africa/Ceuta ⇒ ES)
 *   3. Browser language (es-* ⇒ Spanish UI; es-ES ⇒ ES country hint)
 */

export type SupportedLanguage = "en-US" | "es-ES";

export type RegionSource = "cookie" | "timezone" | "language" | "none";

export interface DetectedRegion {
  /** ISO 3166-1 alpha-2 country code, or null when nothing points at one */
  countryCode: string | null;
  /** Which signal produced `countryCode` */
  source: RegionSource;
  /** Best UI language for this visitor */
  language: SupportedLanguage;
}

/** Cookie name nginx sets from the GeoIP2 country lookup */
export const COUNTRY_COOKIE = "sl_country";

/** localStorage key written by the LanguageSwitcher (explicit user choice) */
export const LANGUAGE_STORAGE_KEY = "shopledger_language";

const SPAIN_TIMEZONES = new Set(["Europe/Madrid", "Atlantic/Canary", "Africa/Ceuta"]);

/** Countries whose timezone alone is a reliable "you are here" signal. */
const TIMEZONE_COUNTRY: Record<string, string> = {};
for (const tz of SPAIN_TIMEZONES) TIMEZONE_COUNTRY[tz] = "ES";

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie
    .split(";")
    .map((c) => c.trim())
    .find((c) => c.startsWith(`${name}=`));
  if (!match) return null;
  const value = decodeURIComponent(match.slice(name.length + 1)).trim().toUpperCase();
  return /^[A-Z]{2}$/.test(value) ? value : null;
}

function readTimezone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;
  } catch {
    return null;
  }
}

function readLanguages(): string[] {
  if (typeof navigator === "undefined") return [];
  const list = (navigator.languages && navigator.languages.length
    ? navigator.languages
    : [navigator.language]) as string[];
  return list.filter(Boolean).map((l) => l.toLowerCase());
}

/** Map any BCP-47 tag to one of the languages we ship. */
export function toSupportedLanguage(tag: string | null | undefined): SupportedLanguage | null {
  if (!tag) return null;
  const lower = tag.toLowerCase();
  if (lower.startsWith("es")) return "es-ES";
  if (lower.startsWith("en")) return "en-US";
  return null;
}

/**
 * Detect the visitor's region from browser signals.
 * Pure read — safe to call at module init and inside render.
 */
export function detectRegion(): DetectedRegion {
  const cookieCountry = readCookie(COUNTRY_COOKIE);
  const tz = readTimezone();
  const languages = readLanguages();

  let countryCode: string | null = null;
  let source: RegionSource = "none";

  if (cookieCountry) {
    countryCode = cookieCountry;
    source = "cookie";
  } else if (tz && TIMEZONE_COUNTRY[tz]) {
    countryCode = TIMEZONE_COUNTRY[tz];
    source = "timezone";
  } else {
    // Only a fully-qualified es-ES tag is a country hint; plain "es" is not.
    const esES = languages.find((l) => l === "es-es");
    if (esES) {
      countryCode = "ES";
      source = "language";
    }
  }

  // Language: the visitor's own preference first, then the country.
  let language: SupportedLanguage | null = null;
  for (const l of languages) {
    language = toSupportedLanguage(l);
    if (language) break;
  }
  if (!language) language = countryCode === "ES" ? "es-ES" : "en-US";

  return { countryCode, source, language };
}

/** True when the visitor appears to be physically in Spain. */
export function isSpainVisitor(region: DetectedRegion = detectRegion()): boolean {
  return region.countryCode === "ES";
}

/** The language the user explicitly picked in Settings, if any. */
export function getStoredLanguage(): SupportedLanguage | null {
  try {
    return toSupportedLanguage(localStorage.getItem(LANGUAGE_STORAGE_KEY));
  } catch {
    return null;
  }
}

export function setStoredLanguage(language: SupportedLanguage | null): void {
  try {
    if (language) localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
    else localStorage.removeItem(LANGUAGE_STORAGE_KEY);
  } catch {
    // localStorage unavailable (private mode, Capacitor edge cases) — ignore
  }
}
